import type { ClassificationSegmentFilter } from '../classification.js';
import type { BillCostProvisionSummary } from '../cost-services/bill-matching.js';
import type {
  ApAllocationType,
  ApBillSource,
  ApBillStatus,
  ApBillType,
  ApClassificationSource,
  ApPaymentStatus,
  ClientReimbursementStatus,
  CreditCardBrand,
  CreditCardSource,
  PaymentMethodType,
} from '../generated/enums.js';
import { StatutoryTreatment } from '../generated/enums.js';
import type { PayrollBillSubType } from '../tenant/tenant-settings.js';
import type { BillOrderByField } from './bill-list-order.js';
import type { IntakeDecisionMatchedRule } from './intake-rules.js';
import { isStatutoryTreatmentOnRails } from './statutory-treatment.js';

export type { BillOrderByField, BillSegmentOrderField } from './bill-list-order.js';
export {
  BILL_LIST_ORDER_BY_FIELDS,
  billSegmentOrderIdentity,
  isBillOrderByField,
  isBillSegmentOrderField,
} from './bill-list-order.js';

/** The posting-book currency available to a principal authorized to create AP bills. */
export interface BillEntityFunctionalCurrencyResponse {
  currency: string | null;
}

export interface BillSortItem {
  field: BillOrderByField;
  direction: 'asc' | 'desc';
}

/** Payments awaiting dispatch or bank confirmation, grouped by source account. */
export type PaymentQueueTimelineBucket = 'OVERDUE' | 'TODAY' | 'NEXT_7_DAYS' | 'LATER';

export interface PaymentQueueSummaryResponse {
  paymentCount: number;
  entityCount: number;
  totals: { currencyCode: string; totalAmount: number }[];
  timeline: {
    bucket: PaymentQueueTimelineBucket;
    paymentCount: number;
    totals: { currencyCode: string; totalAmount: number }[];
  }[];
  groups: PaymentQueueGroupResponse[];
}

export interface PaymentQueueGroupResponse {
  entityId: string;
  entityName: string;
  originBankAccountId: string | null;
  accountName: string | null;
  bankName: string | null;
  accountNumber: string | null;
  currencyCode: string;
  paymentCount: number;
  totalAmount: number;
}

/** Two-step agent mutation lifecycle used by the AP bill write boundary. */
export const BillAgentUpdateAction = {
  PREVIEW: 'PREVIEW',
  EXECUTE: 'EXECUTE',
} as const;
export type BillAgentUpdateAction =
  (typeof BillAgentUpdateAction)[keyof typeof BillAgentUpdateAction];

/** Shared envelope for the internal API used by the MCP AP bill mutation. */
export interface BillAgentUpdateRequest<TPatch, TClassification> {
  action: BillAgentUpdateAction;
  expectedResourceVersion?: string;
  patch?: TPatch;
  classifications?: TClassification[];
}

/** Task-shaped result shared by the API and MCP adapter. */
export interface BillAgentUpdateResponse {
  billId: string;
  status: ApBillStatus;
  changedFields: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  warnings: string[];
  nextAction: string;
  resourceVersion?: string;
}

/** Fields populated by document extraction and kept read-only in the bill editor. */
export interface BillAiExtractedField {
  fieldName: string;
  fieldValue: string;
}

/**
 * Metadata persisted alongside an AP bill when it enters the product.
 * `intakeRules` is deliberately shared with the intake writer so provenance has one contract.
 */
/** A registered vendor an extracted issuer name tied with, offered to the person who picks one. */
export interface VendorResolutionCandidate {
  vendorId: string;
  name: string;
}

export interface BillIngestionMetadata {
  intakeRules?: IntakeDecisionMatchedRule[];
  boletoDigitavelLine?: string;
  nfeAccessKeyRaw?: string;
  aiExtractedFields?: BillAiExtractedField[];
  potentialDuplicate?: {
    billId: string;
    score: number;
    confidence: string;
  };
  /** Set when intake left the vendor empty because the issuer name matched several vendors. */
  vendorResolution?: {
    status: 'AMBIGUOUS';
    candidates: VendorResolutionCandidate[];
  };
  [key: string]: unknown;
}

/** Latest failed approval attempt after the bill entered PENDING_APPROVAL (bill detail API). */
export interface LastApprovalFailure {
  code?: string | null;
  message: string;
  at: string;
  details?: { bookId: string; railParentAccountId: string };
}

export interface SimplifiedBillDocument {
  documentId: string;
  documentType: string;
}

export interface SimplifiedPayment {
  paymentId: string;
  paymentNumber: number;
  scheduledDate: string | null;
  scheduledAmount: number;
  paymentMethod: PaymentMethodType | null;
  status?: ApPaymentStatus;
  bankReturnCode?: string | null;
  bankReturnMessage?: string | null;
  sentToPaymentAt?: string | null;
  originBankAccountId?: string | null;
  originBankAccountName?: string | null;
  inActiveCnabFile?: boolean;
  /**
   * The day a send now pays it: the scheduled day (today when past, in São Paulo) moved to a
   * business day. Computed by the same function every provider's send uses.
   */
  effectivePaymentDate?: string | null;
  /** Which way a non-business day moves: `previous` for a tax guide, `next` otherwise. */
  paymentDateRoll?: 'next' | 'previous';
}

export interface EnrichedBillClassificationForList {
  classificationId: string;
  lineItemId: string | null;
  glAccounts: {
    id: string;
    code?: string;
    name?: string;
    amount: number;
    percent: number;
  }[];
  departments: {
    id: string;
    code?: string;
    name?: string;
    amount: number;
    percent: number;
  }[];
  costCenters: {
    id: string;
    code?: string;
    name?: string;
    amount: number;
    percent: number;
  }[];
  managerialAccounts: {
    id: string;
    code?: string;
    name?: string;
    amount: number;
    percent: number;
  }[];
  segments: {
    segmentId: string;
    segmentValueId: string;
    /** Free-text value for segments with no predefined catalog. */
    textValue?: string;
    segmentName?: string;
    value?: string;
    label?: string | null;
  }[];
}

export interface BillCardAllocationResponse {
  allocationId: string;
  creditCardId: string;
  amount: number;
  creditCardName?: string | null;
  cardholderName?: string | null;
  lastFourDigits?: string | null;
  brand?: CreditCardBrand | string | null;
  source?: CreditCardSource | string | null;
}

export interface SimplifiedBillResponse {
  billId: string;
  entityId?: string;
  entity?: {
    entityId: string;
    name: string | null;
  } | null;
  billType?: ApBillType | string;
  cardProvider?: CreditCardSource | string | null;
  cardIssuerInstitutionId?: string | null;
  cardIssuerInstitutionBankCode?: string | null;
  cardIssuerInstitutionBankName?: string | null;
  vendorBillNumber: string | null;
  description: string | null;
  createdAt?: string | null;
  billIssueDate: string | null;
  billCompetencyDate?: string | null;
  dueDate?: string | null;
  documents: SimplifiedBillDocument[];
  earliestPaymentDueDate: string | null;
  /**
   * ALC-47 programmed payment date from the bill's full payment set: earliest
   * open installment, else latest PAID/RECONCILED. Independent of payment-tab
   * row filters on `payments`.
   */
  scheduledDate: string | null;
  totalAmount: number | null;
  /** Present on API list items; used with subtotal/withholdings for net payable display. */
  netPaymentAmount?: number | null;
  subtotalAmount?: number | null;
  interestAmount?: number | null;
  fineAmount?: number | null;
  irrfWithholdingAmount?: number | null;
  issWithholdingAmount?: number | null;
  pisWithholdingAmount?: number | null;
  cofinsWithholdingAmount?: number | null;
  csllWithholdingAmount?: number | null;
  inssWithholdingAmount?: number | null;
  pisCreditAmount?: number | null;
  cofinsCreditAmount?: number | null;
  totalWithholdingAmount?: number | null;
  /**
   * When `false`, the listed withholding amounts are informational (vendor bears them)
   * and must NOT be subtracted from the net payable for display/aggregation.
   */
  withholdingsIsRetained?: boolean | null;
  currencyCode?: string | null;
  status: ApBillStatus;
  isReimbursement?: boolean;
  isClientReimbursable?: boolean;
  reimbursableCustomerId?: string | null;
  clientReimbursementStatus?: ClientReimbursementStatus | null;
  debitNoteId?: string | null;
  isRecurring?: boolean;
  paymentMethod?: PaymentMethodType | string | null;
  /** Distinct payment methods from visible list payment rows (header fallback when none). */
  paymentMethods?: PaymentMethodType[];
  payments: SimplifiedPayment[];
  /**
   * Present when the bill is a reimbursement: `requested_amount` from the linked reimbursement request.
   */
  reimbursementRequestedAmount?: number | null;
  payeeName?: string | null;
  requestedByUser?: { name: string } | null;
  vendor?: {
    vendorId: string;
    legalName: string | null;
    tradeName: string | null;
    cnpj: string | null;
  } | null;
  /** Bill creation / ingestion channel (ApBillSource). */
  source?: ApBillSource | string | null;
  sourcePayrollRunId?: string | null;
  /**
   * Payroll subtype of a payroll-generated bill (`SALARY`, `VACATION`, `TERMINATION_NET`,
   * `THIRTEENTH`, `RPA`, a repasse subtype, ...), read from `customFields.payrollSubType`; null
   * for any other bill. Lets the list label "Folha (férias)" instead of a generic payroll badge.
   */
  payrollBillSubType?: string | null;
  /** ISO country of the bill's entity; picks which withholdings and credits the bill shows. */
  entityCountry?: string | null;
  /**
   * Detail only: the entity country, 'US' or 'MX', for the country-specific AP rules
   * (net coherence and IVA/ISR withholding edits); null for other countries.
   */
  usMxApJurisdiction?: 'US' | 'MX' | null;
  cardAllocations?: BillCardAllocationResponse[];
  classifications?: EnrichedBillClassificationForList[];
  /**
   * Most recent approval failure recorded after the bill last entered PENDING_APPROVAL.
   * Only populated for PENDING_APPROVAL rows — the same gate the bill detail page uses to
   * decide between "approve" and "retry approval".
   */
  lastApprovalFailure?: LastApprovalFailure | null;
}

export interface McpBillDetailResponse {
  billId: string;
  billType?: ApBillType | string | null;
  cardProvider?: CreditCardSource | string | null;
  cardIssuerInstitutionId?: string | null;
  cardIssuerInstitutionBankCode?: string | null;
  cardIssuerInstitutionBankName?: string | null;
  vendorBillNumber?: string | null;
  description?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  billIssueDate?: string | null;
  billCompetencyDate?: string | null;
  billManagerialDate?: string | null;
  dueDate?: string | null;
  totalAmount?: number | null;
  netPaymentAmount?: number | null;
  subtotalAmount?: number | null;
  originalCurrencyCode?: string | null;
  originalAmount?: number | null;
  interestAmount?: number | null;
  fineAmount?: number | null;
  irrfWithholdingAmount?: number | null;
  issWithholdingAmount?: number | null;
  pisWithholdingAmount?: number | null;
  cofinsWithholdingAmount?: number | null;
  csllWithholdingAmount?: number | null;
  inssWithholdingAmount?: number | null;
  totalWithholdingAmount?: number | null;
  currencyCode?: string | null;
  billStatus?: ApBillStatus | string | null;
  status?: ApBillStatus | string | null;
  isReimbursement?: boolean | null;
  isClientReimbursable?: boolean | null;
  reimbursableCustomerId?: string | null;
  clientReimbursementStatus?: ClientReimbursementStatus | string | null;
  debitNoteId?: string | null;
  isRecurring?: boolean | null;
  paymentMethod?: PaymentMethodType | string | null;
  withholdingsIsRetained?: boolean | null;
  source?: ApBillSource | string | null;
  sourcePayrollRunId?: string | null;
  vendor?: {
    vendorId: string | null;
    legalName: string | null;
    tradeName: string | null;
    cnpj: string | null;
  } | null;
  entity?: {
    entityId: string | null;
    name: string | null;
    tradeName: string | null;
    cnpj: string | null;
  } | null;
  cfop?: {
    cfopCode: string | null;
    description: string | null;
  } | null;
  requestedByUser?: {
    userId: string | null;
    name: string | null;
    email: string | null;
  } | null;
  lineItems?: {
    lineItemId: string | null;
    lineNumber: number | null;
    description: string | null;
    quantity: number | null;
    unitPrice: number | null;
    totalAmount: number | null;
  }[];
  classifications?: {
    classificationId: string | null;
    lineItemId: string | null;
    allocationType: string | null;
    glAccounts: unknown[];
    departments: unknown[];
    costCenters: unknown[];
    managerialAccounts: unknown[];
    segments: unknown[];
    customBreakdown: unknown[];
    notes: string | null;
    source: string | null;
  }[];
  alerts?: {
    alertId: string | null;
    alertType: string | null;
    severity: string | null;
    message: string | null;
    createdAt: string | null;
  }[];
  paymentBeneficiaryEmployee?: {
    employeeId: string | null;
    employeeNumber: string | null;
    legalName: string | null;
    tradeName: string | null;
  } | null;
  paymentBeneficiaryVendor?: {
    vendorId: string | null;
    legalName: string | null;
    tradeName: string | null;
  } | null;
  sourcePayrollRun?: {
    payrollRunId: string | null;
    periodId: string | null;
    status: string | null;
  } | null;
  recurrenceType?: string | null;
  variableComparisonMonths?: number | null;
  recurringTemplateAmount?: number | null;
  recurringTemplatePeriodLimitAmount?: number | null;
  vendorRecurringBillName?: string | null;
  expectedDueDayOfMonth?: number | null;
  expectedCompetencyDayOfMonth?: number | null;
  cardAllocations?: BillCardAllocationResponse[];
  /** ALC-4476 — "Baixa de provisão: R$X de R$Y" and the matches per line; absent when none. */
  costProvision?: BillCostProvisionSummary | null;
}

export interface FindBillsQuery {
  /**
   * With no `status` and no `statusIn` (or `status: 'all'`), MIGRATED bills are hidden.
   * Name MIGRATED in either field to include them (the web "Todas" tab does).
   */
  status?: ApBillStatus;
  statusIn?: ApBillStatus[];
  billTypes?: ApBillType[];
  vendorId?: string;
  vendorIds?: string[];
  entityId?: string;
  search?: string;
  vendorBillNumber?: string;
  originBankAccountId?: string;
  bankReturnMessageContains?: string;
  paymentStatusIn?: ApPaymentStatus[];
  isAutomaticBankPayment?: boolean;
  billIssueDateFrom?: string;
  billIssueDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  /**
   * Inclusive UTC day window on the ALC-47 list scheduled date: earliest open
   * payment (SCHEDULED/PENDING/PROCESSING), else latest PAID/RECONCILED.
   * Cancelled/failed installments never match.
   */
  scheduledDateFrom?: string;
  scheduledDateTo?: string;
  paymentDocumentDueDateFrom?: string;
  paymentDocumentDueDateTo?: string;
  paymentScheduledDateFrom?: string;
  paymentScheduledDateTo?: string;
  paymentAmountEq?: string;
  paymentAmountGte?: string;
  paymentAmountLte?: string;
  openPaymentOverdue?: boolean;
  billCompetencyDateFrom?: string;
  billCompetencyDateTo?: string;
  reimbursementsOnly?: boolean;
  clientReimbursablesOnly?: boolean;
  reimbursableCustomerId?: string;
  clientReimbursementStatusIn?: ClientReimbursementStatus[];
  isRecurring?: boolean;
  paymentMethod?: PaymentMethodType;
  paymentMethodIn?: PaymentMethodType[];
  /**
   * When true, also match bills with no effective list payment method (the `—` cell):
   * no payment registered and no header method, or payments whose visible rows all lack a method.
   * ORs with `paymentMethodIn` / `paymentMethod` when those are set.
   */
  paymentMethodNone?: boolean;
  classificationCostCenterIds?: string[];
  classificationDepartmentIds?: string[];
  classificationGlAccountIds?: string[];
  classificationManagerialAccountIds?: string[];
  categoriaSegmentValueIds?: string[];
  /**
   * Custom classification segments. AND across filters, OR across catalog value ids
   * of the same segment. TEXT is a case-insensitive partial match; digit-only queries
   * also match values that differ only by punctuation (CNPJ).
   */
  classificationSegmentFilters?: ClassificationSegmentFilter[];
  descriptionContains?: string;
  withholdingsOnly?: boolean;
  withTaxCreditsOnly?: boolean;
  withRetainedAmountOnly?: boolean;
  sourceIn?: ApBillSource[];
  sourceNotIn?: ApBillSource[];
  excludeAiOnlyBills?: boolean;
  createdAtFrom?: string;
  createdAtTo?: string;
  totalAmountEq?: string;
  totalAmountGte?: string;
  totalAmountLte?: string;
  includeVendor?: boolean;
  includeLineItems?: boolean;
  includeClassifications?: boolean;
  simplified?: boolean;
  paymentRows?: boolean;
  sourcePayrollRunId?: string;
  payrollSubType?: PayrollBillSubType;
  includeFees?: boolean;
  page?: number;
  limit?: number;
  orderBy?: string[];
}

export interface FindBillsSummaryQuery {
  dueDateFrom?: string;
  dueDateTo?: string;
  openPaymentOverdue?: boolean;
  billTypes?: ApBillType[];
  sourcePayrollRunId?: string;
  /** Only the bills of this entity. */
  entityId?: string;
}

export interface BillsSummaryResponse {
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
  totalCount: number;
  paidCount: number;
  pendingCount: number;
  overdueCount: number;
}

export interface PaginatedBillsResponse<TBill> {
  data: TBill[];
  total: number;
  page: number;
  limit: number;
}

/** Maximum number of bills accepted by a synchronous bulk action request. */
export const MAX_BULK_BILL_ACTION_COUNT = 50;

export const BULK_BILL_ENTITY_CHANGE_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'PENDING_APPROVAL',
] as const satisfies readonly ApBillStatus[];

export interface BulkBillEntityChangeRequest {
  billIds: string[];
  entityId: string;
  clearClassificationOnEntityChange: true;
  /** Subset of billIds that may only retry classification, never transfer entities. */
  retryClassificationBillIds?: string[];
}

export type BillEntityChangeClassificationStatus =
  'QUEUED' | 'NO_DOCUMENT' | 'PENDING' | 'NOT_REQUESTED';

/**
 * Why a custom field (segment) of the bill cannot follow it to the destination entity.
 * - NO_MATCHING_FIELD: the destination entity has no active field with the same name.
 * - TYPE_MISMATCH: the same-named field has another data type, or free text vs. a list.
 * - NO_MATCHING_VALUE: the list field has no active option with the same text.
 * - SPLIT_VALUES: the field is split across several values on one classification.
 * - VARYING_VALUES: line items or allocation rows disagree on the value (or some lack it).
 * - AMBIGUOUS_MATCH: no exact match, and several fields (or options) match once case and
 *   spaces are ignored, so none is picked.
 */
export type EntityChangeSegmentDropReason =
  | 'NO_MATCHING_FIELD'
  | 'TYPE_MISMATCH'
  | 'NO_MATCHING_VALUE'
  | 'SPLIT_VALUES'
  | 'VARYING_VALUES'
  | 'AMBIGUOUS_MATCH';

export interface EntityChangeCarriedSegment {
  segmentName: string;
  value: string;
}

export interface EntityChangeDroppedSegment {
  segmentName: string;
  /** Null when the value differs across line items or allocations. */
  value: string | null;
  reason: EntityChangeSegmentDropReason;
}

/** What a consented entity change keeps and drops of the bill's custom fields. */
export interface BillEntityChangePreview {
  carried: EntityChangeCarriedSegment[];
  dropped: EntityChangeDroppedSegment[];
  /** Required custom fields of the destination entity that stay empty after the change. */
  missingRequired: string[];
}

export interface BulkBillEntityChangeResult {
  billId: string;
  outcome: 'CHANGED' | 'UNCHANGED' | 'FAILED';
  classificationStatus: BillEntityChangeClassificationStatus;
  error?: string;
  errorCode?: string;
}

export interface BulkBillEntityChangeResponse {
  total: number;
  changed: number;
  unchanged: number;
  failed: number;
  results: BulkBillEntityChangeResult[];
}

export const BULK_BILL_TRANSITION_TARGET_STATUSES = [
  'PENDING_APPROVAL',
  'VOID',
] as const satisfies readonly ApBillStatus[];

export type BulkBillTransitionTargetStatus = (typeof BULK_BILL_TRANSITION_TARGET_STATUSES)[number];

export interface BulkBillStatusTransitionRequest {
  billIds: string[];
  targetStatus: BulkBillTransitionTargetStatus;
  reason?: string;
  /**
   * Warning codes the submitter confirmed for this batch. Each bill records only the
   * codes that actually apply to it, so sending the union the dialog displayed is safe.
   */
  acknowledgedWarnings?: string[];
}

export interface BulkBillStatusTransitionResult {
  billId: string;
  success: boolean;
  previousStatus?: ApBillStatus;
  finalStatus?: ApBillStatus;
  error?: string;
  errorCode?: string;
}

export interface BulkBillStatusTransitionResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkBillStatusTransitionResult[];
}

/**
 * Bulk approve/reject act on the bill's current approval workflow step — the same path
 * as the "Aprovar"/"Rejeitar" buttons on Bill Details — not a direct status flip. Only
 * bills in PENDING_APPROVAL are eligible; ineligible bills are reported per-bill rather
 * than failing the whole batch.
 */
export interface BulkBillApprovalRequest {
  billIds: string[];
  /** Optional comment recorded on the approval step, same as the single-bill Aprovar dialog. */
  comment?: string;
  /** Warning codes explicitly confirmed for this approval batch. */
  acknowledgedWarnings?: string[];
}

export interface BulkBillRejectionRequest {
  billIds: string[];
  /** Required rejection reason, same as the single-bill Rejeitar dialog. */
  reason: string;
}

/**
 * What acting on one bill does — the distinction the listing has to make explicit, because
 * "approved" and "your step approved, now someone else's turn" look identical from a status
 * column but are very different outcomes for the person clicking.
 *
 * - `FULLY_APPROVES`  — this approval completes the chain: the bill becomes APPROVED and posts.
 * - `ADVANCES_TO_NEXT` — this approval clears the user's step; the bill stays PENDING_APPROVAL
 *   waiting on other approvers.
 * - `RETRIES`         — the bill is stuck/orphaned, so the action routes through retry-approval
 *   (the same thing the Retry button on Bill Details does).
 * - `REJECTS`         — rejection ends the whole chain and the bill becomes REJECTED.
 * - `BLOCKED`         — nothing will happen to this bill; see `blockCode`.
 */
export const BULK_BILL_APPROVAL_OUTCOMES = [
  'FULLY_APPROVES',
  'ADVANCES_TO_NEXT',
  'RETRIES',
  'REJECTS',
  'BLOCKED',
] as const;
export type BulkBillApprovalOutcome = (typeof BULK_BILL_APPROVAL_OUTCOMES)[number];

/**
 * Why a selected bill will be skipped. Stable codes rather than prose: the listing renders
 * these through i18n, so the API must not be the source of user-facing copy.
 */
export const BULK_BILL_APPROVAL_BLOCK_CODES = [
  'BILL_NOT_FOUND',
  'BULK_STATUS_NOT_ELIGIBLE',
  'NO_ACTIVE_APPROVAL_CHAIN',
  /** The user already approved their step; the chain is waiting on someone else. */
  'ALREADY_APPROVED_BY_USER',
  /** The user approves a later step; the current step belongs to another approver. */
  'AWAITING_OTHER_APPROVER',
  'NO_ACTIONABLE_STEP',
  /** The step was escalated; the approval transaction only accepts PENDING/IN_PROGRESS. */
  'STEP_ESCALATED',
  'UNAUTHORIZED',
  /**
   * The bill could not be checked at all. Distinct from every other code here, which states
   * something definite about the approval: this one admits the projection failed, and is the
   * only one worth retrying.
   */
  'PREVIEW_UNAVAILABLE',
] as const;
export type BulkBillApprovalBlockCode = (typeof BULK_BILL_APPROVAL_BLOCK_CODES)[number];

export const BULK_BILL_APPROVAL_PREVIEW_ACTIONS = ['APPROVE', 'REJECT'] as const;
export type BulkBillApprovalPreviewAction = (typeof BULK_BILL_APPROVAL_PREVIEW_ACTIONS)[number];

export interface BulkBillApprovalPreviewRequest {
  billIds: string[];
  action: BulkBillApprovalPreviewAction;
}

export interface BulkBillApprovalPreviewItem {
  billId: string;
  outcome: BulkBillApprovalOutcome;
  blockCode?: BulkBillApprovalBlockCode;
  status?: ApBillStatus;
  vendorBillNumber?: string | null;
  vendorName?: string | null;
  description?: string | null;
  totalAmount?: number | null;
  currencyCode?: string | null;
  /** 1-based position of the step this user would act on, among the chain's approval steps. */
  currentStepNumber?: number | null;
  /** Total approval steps in the chain (submission step excluded). */
  totalSteps?: number | null;
  /** Approvers who must still act after this user — empty when the action finishes the chain. */
  remainingApproverNames?: string[];
  /** Consent warnings that apply if this action reaches final approval. */
  warnings?: BulkBillReadinessError[];
}

/**
 * Read-only projection of what a bulk approve/reject would do, so the confirmation dialog can
 * state it before anything is mutated. It reads the chains outside the approval lock, so it is
 * "what would happen now" — the authority remains the approval transaction itself.
 */
export interface BulkBillApprovalPreviewResponse {
  total: number;
  fullyApproves: number;
  advances: number;
  retries: number;
  rejects: number;
  blocked: number;
  items: BulkBillApprovalPreviewItem[];
}

export interface BulkBillApprovalActionResult {
  billId: string;
  success: boolean;
  previousStatus?: ApBillStatus;
  finalStatus?: ApBillStatus;
  /** Always derived from what actually happened, never carried over from a preview. */
  outcome?: BulkBillApprovalOutcome;
  blockCode?: BulkBillApprovalBlockCode;
  error?: string;
  errorCode?: string;
}

export interface BulkBillApprovalActionResponse {
  total: number;
  succeeded: number;
  failed: number;
  /** Of `succeeded`, how many bills reached APPROVED. */
  fullyApproved: number;
  /** Of `succeeded`, how many only cleared this user's step and still await other approvers. */
  advanced: number;
  results: BulkBillApprovalActionResult[];
}

export interface BulkBillSendToApprovalValidationRequest {
  billIds: string[];
}

export interface BulkBillReadinessError {
  field: string;
  message: string;
  code?: string;
  /**
   * Structured facts behind the message, when the check has them. Cost-match checks (ALC-4476)
   * send `{ lineItemId, lineNumber, entryId, currency, varianceAmount, tolerance }`; the
   * out-of-tolerance variance warning is confirmed by echoing
   * `COST_MATCH_VARIANCE_OUT_OF_TOLERANCE` in `acknowledgedWarnings`.
   */
  details?: Record<string, unknown>;
}

export interface BulkBillSendToApprovalValidationItem {
  billId: string;
  ready: boolean;
  status?: ApBillStatus;
  vendorBillNumber?: string | null;
  vendorName?: string | null;
  description?: string | null;
  totalAmount?: number | null;
  currencyCode?: string | null;
  errors: BulkBillReadinessError[];
  /**
   * Readiness gaps that do NOT block the submission (a PIX payment with no key, a
   * payee account nobody verified). The submitting client shows these and echoes the
   * codes back as `acknowledgedWarnings` on the transition, which records the consent
   * in bill history.
   */
  warnings?: BulkBillReadinessError[];
}

export interface BulkBillSendToApprovalValidationResponse {
  total: number;
  ready: number;
  blocked: number;
  items: BulkBillSendToApprovalValidationItem[];
}

/** Bill proposal data from AI chat present_bill_summary tool (for confirmation UI) */
export interface BillProposalRecentBill {
  billId?: string;
  amount: number;
  description?: string;
  dueDate: string;
  status: string;
}

/** Source e-mail linked to a bill (`op_email_message` via `bill_id` / `bill_ids`). */
export interface BillSourceEmailResponse {
  messageId: string;
  subject: string | null;
  fromAddress: string;
  fromName: string | null;
  receivedAt: string;
  bodyPlain: string | null;
  bodyHtml: string | null;
}

/** Raw/normalized payload submitted through the External API for this bill. */
export interface BillSourceApiPayloadResponse {
  billId: string;
  externalReferenceId: string | null;
  intakeId?: string | null;
  submittedAt: string | null;
  payload: unknown;
}

export interface BillClassificationReplicationCandidate {
  billId: string;
  vendorBillNumber: string | null;
  description: string | null;
  billIssueDate: string | null;
  dueDate: string | null;
  totalAmount: number | null;
  status: ApBillStatus;
}

export interface BillClassificationReplicationCandidatesResponse {
  sourceBillId: string;
  sourceVendorId: string;
  candidates: BillClassificationReplicationCandidate[];
}

export interface ReplicateBillClassificationsRequest {
  targetBillIds: string[];
}

export interface ReplicateBillClassificationResult {
  billId: string;
  success: boolean;
  classificationsCount: number;
  error?: string;
}

export interface ReplicateBillClassificationsResponse {
  sourceBillId: string;
  total: number;
  succeeded: number;
  failed: number;
  results: ReplicateBillClassificationResult[];
}

export interface BillProposalData {
  vendor: { id: string; name: string; taxId?: string };
  amount: number;
  dueDate: string;
  description?: string;
  classification?: {
    glAccountCode?: string;
    glAccountName?: string;
    costCenterCode?: string;
    costCenterName?: string;
    managerialAccountCode?: string;
    managerialAccountName?: string;
    departmentCode?: string;
    departmentName?: string;
  };
  recentBills?: BillProposalRecentBill[];
  isRecurring?: boolean;
  recurrencyFrequency?: string;
  entityId?: string;
}

/** Stable codes for net payable amount edits (stored in bill history, not UI copy). */
export const NET_PAYMENT_AMOUNT_CHANGE_REASON_VALUES = [
  'AI_EXTRACTION_ERROR',
  'ADJUSTMENTS',
  'OTHER',
] as const;

export type NetPaymentAmountChangeReasonCode =
  (typeof NET_PAYMENT_AMOUNT_CHANGE_REASON_VALUES)[number];

/** Same stable codes as net payable edits; stored on gross/subtotal amount changes. */
export const SUBTOTAL_AMOUNT_CHANGE_REASON_VALUES = NET_PAYMENT_AMOUNT_CHANGE_REASON_VALUES;

export type SubtotalAmountChangeReasonCode = NetPaymentAmountChangeReasonCode;

export interface BillTimelineActor {
  userId?: string;
  name: string;
  initials: string;
}

export interface BillTimelineClassificationSplit {
  id: string;
  code?: string;
  name?: string;
  amount?: number | null;
  percent?: number | null;
}

export interface BillTimelineClassificationSegmentSplit {
  segmentId: string;
  segmentValueId: string;
  /** Free-text value for segments with `hasPredefinedValues = false`. */
  textValue?: string;
  segmentName?: string;
  value?: string;
  label?: string | null;
  amount?: number | null;
  percent?: number | null;
}

export type BillTimelineClassificationDimensionKind =
  'glAccounts' | 'departments' | 'costCenters' | 'managerialAccounts' | 'segments';

export interface BillTimelineClassificationDimension {
  kind: BillTimelineClassificationDimensionKind;
  label: string;
  splits: BillTimelineClassificationSplit[] | BillTimelineClassificationSegmentSplit[];
}

export interface BillTimelineClassificationCustomBreakdown {
  label?: string;
  amount?: number | null;
  percent?: number | null;
  dimensions: BillTimelineClassificationDimension[];
}

export interface BillTimelineClassificationSnapshot {
  classificationId?: string;
  lineItemId?: string | null;
  lineItemLabel?: string | null;
  allocationType?: ApAllocationType | string | null;
  source?: ApClassificationSource | string | null;
  dimensions: BillTimelineClassificationDimension[];
  customBreakdown?: BillTimelineClassificationCustomBreakdown[];
  notes?: string | null;
}

export interface BillTimelineClassificationFieldChange {
  field: string;
  label: string;
  before?: BillTimelineClassificationSnapshot | string | null;
  after?: BillTimelineClassificationSnapshot | string | null;
}

export interface BillTimelineClassificationDetail {
  type: 'created' | 'updated' | 'mode_changed';
  snapshot?: BillTimelineClassificationSnapshot;
  changes?: BillTimelineClassificationFieldChange[];
  summary?: string[];
}

export interface BillTimelineApprovalAutomation {
  type: 'workflow_auto_approved' | 'no_workflow_match';
  workflowId?: string;
  workflowName?: string;
  workflowUrl?: string;
  reason?: string;
}

export interface BillTimelineReconciliationLink {
  originId: string;
  originType: 'BANK_TX' | 'CARD_ITEM';
  groupId?: string;
  reconciliationUrl: string;
}

export interface BillTimelineComment {
  kind: 'comment';
  id: string;
  createdAt: string;
  actor: BillTimelineActor;
  content: string;
  mentions: string[];
}

/** A payment change made by reconciliation for a payment mismatch (ADR 0031). */
export interface BillTimelinePaymentMismatch {
  kind: 'PAYMENT_CANCELLED' | 'REMAINDER_SCHEDULED' | 'PAYMENT_RESTORED' | 'REMAINDER_REMOVED';
  /** The cancelled payment's amount, or the new remainder payment's amount. */
  amount: number;
  /** The bank movement that caused the change. */
  bankAmount: number;
  /** ISO 4217 code of both amounts: the functional currency of the bill's book. */
  currency?: string;
  justification?: string;
  bankCancellationConfirmed?: boolean;
}

/** One field named by a vendor rule divergence history entry. Values are the document and the rule. */
export interface BillTimelineVendorRuleDivergenceItem {
  field: string;
  kind: 'withholding' | 'pis_cofins_credit';
  documentValue: number | boolean | string | null;
  ruleValue: number | boolean | string | null;
}

/**
 * Structured vendor rule divergence activity (ALC-4559). The timeline renders a sentence from this
 * instead of the stored JSON.
 */
export interface BillTimelineVendorRuleDivergence {
  event: 'DETECTED' | 'CLEARED' | 'RESOLVED';
  option?: 'KEEP_DOCUMENT' | 'APPLY_RULE' | 'NEW_RULE_VERSION';
  /** `YYYY-MM` the new rule version starts, when the choice was a new version. */
  competence?: string;
  items?: BillTimelineVendorRuleDivergenceItem[];
}

export interface BillTimelineActivity {
  kind: 'activity';
  id: string;
  createdAt: string;
  actor: BillTimelineActor;
  message: {
    text: string;
    detail?: string;
    classification?: BillTimelineClassificationDetail;
    approvalAutomation?: BillTimelineApprovalAutomation;
    reconciliationLink?: BillTimelineReconciliationLink;
    paymentMismatch?: BillTimelinePaymentMismatch;
    /**
     * A vendor rule divergence event (ALC-4559). The sentence is also in `text` (pt-BR); the bill
     * screen uses this to say the same thing in the person's language, with no raw JSON.
     */
    vendorRuleDivergence?: BillTimelineVendorRuleDivergence;
  };
  raw?: {
    entityType: string;
    action: string;
    entityId: string;
  };
}

export type BillTimelineItem = BillTimelineComment | BillTimelineActivity;

export interface BillTimelineResponse {
  items: BillTimelineItem[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** Shared predicate: bill classifications are locked while linked to a recurring template. */
export function isBillClassificationLockedByRecurringTemplate(bill: {
  vendorRecurringBillId?: string | null;
  isRecurring?: boolean | null;
}): boolean {
  return Boolean(bill.vendorRecurringBillId && bill.isRecurring !== false);
}

/**
 * Bills whose classification section is hidden because no human picks the account.
 *
 * Keyed on TREATMENT, not on statutory provenance (ALC-1504): `BYPASS` posts nothing on approval and
 * `AUTO_PROVISION` derives both legs from the tax slot, so neither needs the dimension pickers. A
 * `CLASSIFY` guia — a tax paid on behalf of a client — is an ordinary expense that must be
 * classified, even though it demonstrably came from a guia.
 *
 * Callers that only have the legacy boolean (no treatment loaded) still get the old answer, so a
 * partial select cannot silently start showing the section for a bypassed bill.
 */
export function isBillClassificationBypassed(bill: {
  billType?: string | null;
  isFromStatutoryDocument?: boolean | null;
  statutoryTreatment?: StatutoryTreatment | null;
}): boolean {
  if (bill.billType === 'CREDIT_CARD_INVOICE') return true;
  if (bill.isFromStatutoryDocument !== true) return false;
  return bill.statutoryTreatment == null
    ? true
    : isStatutoryTreatmentOnRails(bill.statutoryTreatment);
}

/**
 * A statutory `CLASSIFY` bill follows the ordinary expense-classification flow.
 * The statutory assignment decision starts as a liability because tax-slot bills normally settle
 * an existing payable; carrying that context into `CLASSIFY` would offer the agent only liability
 * GL accounts and then discard its suggestion as invalid for an expense bill.
 */
export function resolveBillClassificationAccountingContext(
  bill: {
    isFromStatutoryDocument?: boolean | null;
    statutoryTreatment?: StatutoryTreatment | null;
  },
  fallback?: string,
): string | undefined {
  return bill.isFromStatutoryDocument === true && bill.statutoryTreatment === 'CLASSIFY'
    ? 'EXPENSE'
    : fallback;
}

/**
 * Recurrence fields cleared when a pre-approval bill transitions to BYPASS (ALC-1646).
 */
export const BILL_RECURRENCE_CLEAR_DATA = {
  isRecurring: false,
  recurringReasoning: null,
  recurrencyFrequency: null,
  vendorRecurringBillId: null,
  installmentNumberRecurringBill: null,
} as const;

/** Automated inference/reconciliation must never elevate statutory bills to recurring. */
export function shouldBlockAutomatedRecurrence(bill: {
  isFromStatutoryDocument?: boolean | null;
}): boolean {
  return bill.isFromStatutoryDocument === true;
}

/**
 * BYPASS statutory guides must never be recurring (ALC-1646).
 *
 * Provisioned tax obligations are already projected via liabilities; a recurring template would
 * double-count them in cashflow. CLASSIFY / AUTO_PROVISION guides may still be marked manually.
 */
export function isStatutoryBypassRecurrenceForbidden(bill: {
  isFromStatutoryDocument?: boolean | null;
  statutoryTreatment?: StatutoryTreatment | null;
}): boolean {
  return (
    bill.isFromStatutoryDocument === true && bill.statutoryTreatment === StatutoryTreatment.BYPASS
  );
}

/**
 * Recurring templates must never attach to consolidated credit-card invoice bills or BYPASS
 * statutory guides.
 */
export function isBillRecurringTemplateBlocked(bill: {
  billType?: string | null;
  isFromStatutoryDocument?: boolean | null;
  statutoryTreatment?: StatutoryTreatment | null;
}): boolean {
  if (bill.billType === 'CREDIT_CARD_INVOICE') return true;
  return isStatutoryBypassRecurrenceForbidden(bill);
}

/** True when any recurrence flag/link is present and should be wiped on BYPASS transition. */
export function billHasRecurrenceData(bill: {
  isRecurring?: boolean | null;
  recurringReasoning?: string | null;
  recurrencyFrequency?: string | null;
  vendorRecurringBillId?: string | null;
  installmentNumberRecurringBill?: number | null;
}): boolean {
  return Boolean(
    bill.isRecurring ||
    bill.recurringReasoning ||
    bill.recurrencyFrequency ||
    bill.vendorRecurringBillId ||
    bill.installmentNumberRecurringBill,
  );
}
